import { ContentObserver } from '@angular/cdk/observers';
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  SkipSelf,
} from '@angular/core';
import * as Rx from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ReadMoreHelper } from './read-more.helper';

export enum FontType {
  PARAGRAPH = 'paragraph',
  SPAN = 'span',
  STRONG = 'strong',
  CONTAINER = 'container',
}

export enum TextSize {
  XXXSMALL = 'xxxsmall',
  XXSMALL = 'xxsmall',
  XSMALL = 'xsmall',
  SMALL = 'small',
  MID = 'mid',
  LARGE = 'large',
  XLARGE = 'xlarge',
  XXLARGE = 'xxlarge',
  XXXLARGE = 'xxxlarge',
  INHERIT = 'inherit',
}

export enum FontColor {
  DARK = 'dark',
  MID = 'mid',
  LIGHT = 'light',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
  INHERIT = 'inherit',
  PROMO = 'promo',
  RECRUITER = 'recruiter',
}

export enum FontWeight {
  BOLD = 'bold',
  NORMAL = 'normal',
}

export enum FontStyle {
  ITALIC = 'italic',
  NORMAL = 'normal',
}

export enum TextAlign {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum TextTransform {
  CAPITALIZE = 'capitalize',
  LOWERCASE = 'lowercase',
  UPPERCASE = 'uppercase',
}

@Component({
  selector: 'fl-text',
  template: `
    <ng-container [ngSwitch]="fontType">
      <div
        class="NativeElement"
        *ngSwitchCase="FontType.PARAGRAPH"
        role="paragraph"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="displayLineBreaks"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </div>
      <span
        class="NativeElement"
        *ngSwitchCase="FontType.SPAN"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="displayLineBreaks"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </span>
      <strong
        class="NativeElement Strong"
        *ngSwitchCase="FontType.STRONG"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="displayLineBreaks"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </strong>
      <!-- this one's also a div but doesn't have paragraph semantics -->
      <div
        class="NativeElement"
        *ngSwitchCase="FontType.CONTAINER"
        [attr.data-text-transform]="textTransform"
        [attr.data-color]="color"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-weight]="weight"
        [attr.data-style]="style"
        [attr.data-line-break]="displayLineBreaks"
      >
        <ng-container *ngTemplateOutlet="injectedContent"></ng-container>
      </div>
      <ng-template #injectedContent>
        <ng-content></ng-content>
      </ng-template>
    </ng-container>
  `,
  styleUrls: ['./text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextComponent
  implements AfterViewInit, OnDestroy, OnChanges, OnInit {
  FontType = FontType;

  @Input() color = FontColor.DARK;

  /**
   * Font size for mobile and above
   */
  @Input() size: TextSize = TextSize.XSMALL;

  /**
   * Change the [size] from tablet and above
   */
  @Input() sizeTablet?: TextSize;

  /**
   * Change the [size] and/or [sizeTablet] from desktop and above
   */
  @Input() sizeDesktop?: TextSize;

  @Input() style = FontStyle.NORMAL;

  /** Change the text-align property (only works on fontType.PARAGRAPH) */
  @HostBinding('attr.data-text-align')
  @Input()
  textAlign?: TextAlign;

  @Input() textTransform?: TextTransform;

  @Input() weight = FontWeight.NORMAL;

  /** Defines the HTML node it will use e.g (<p>, <span>, <strong>)
   *  Note: This is for semantics usage which defaults to a <p> tag
   */
  @HostBinding('attr.data-type')
  @Input()
  fontType = FontType.PARAGRAPH;

  /**
   * Default false, true will parse the \n, <br> tags in the string to create a new line
   * Will break the line when readmore=false, or readmore=true and readmore clicked
   */
  @Input()
  displayLineBreaks = false;

  /*
   * Maximum number of lines of text to display before truncating & display a
   * "Read More" button
   */
  @Input()
  @HostBinding('attr.data-read-more')
  readMore: number;

  @HostBinding('style.max-height')
  maxHeight: string;

  @HostBinding('style.overflow')
  overflow: string;

  private readMoreHelper: ReadMoreHelper;
  private container: HTMLElement;
  private windowSize: { width?: number; height?: number } = {};
  private originalContent: string[] = [];
  private mutationSub?: Rx.Subscription;
  private isExpanding: boolean;
  private isExpanded: boolean;
  private unsubResize?: () => void;

  constructor(
    private element: ElementRef,
    private obs: ContentObserver,
    private ngZone: NgZone,
    @SkipSelf() protected renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit() {
    if (this.readMore) {
      // FIXME: share CSS variables with JS
      const lineHeightUi = 1.43;
      const fontSizeUi = 14;
      this.maxHeight = `${this.readMore * lineHeightUi * fontSizeUi}px`;
      this.overflow = 'hidden';
    }
  }

  ngAfterViewInit() {
    if (this.readMore && isPlatformBrowser(this.platformId)) {
      [this.container] = this.element.nativeElement.children;
      this.ngZone.onStable
        .asObservable()
        .pipe(
          filter(() => !!this.container.scrollHeight),
          take(1),
        )
        .toPromise()
        .then(() => {
          this.truncate();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.readMore &&
      isPlatformBrowser(this.platformId) &&
      this.container &&
      'readMore' in changes
    ) {
      this.truncate();
    }
  }

  ngOnDestroy() {
    this.destroyListeners();
  }

  destroyListeners() {
    if (this.mutationSub) {
      this.mutationSub.unsubscribe();
    }
    if (this.unsubResize) {
      this.unsubResize();
    }
  }

  truncate() {
    this.isExpanded = false;
    this.destroyListeners();
    [this.container] = this.element.nativeElement.children;
    // save original content
    Array.from(this.container.childNodes).forEach((node, index) => {
      if (this.isTextNode(node)) {
        this.originalContent[index] = node.textContent || '';
      }
    });
    this.readMoreHelper = new ReadMoreHelper(this.container, isTruncated => {
      if (!isTruncated) {
        this.isExpanding = true;
        this.isExpanded = true;
        this.renderer.setStyle(
          this.element.nativeElement,
          'max-height',
          'none',
        );
        Array.from(this.container.childNodes).forEach((node, index) => {
          if (this.isTextNode(node)) {
            node.textContent = this.originalContent[index];
          }
        });
        requestAnimationFrame(() => {
          this.isExpanding = false;
        });
      }
    });
    this.readMoreHelper.truncate(this.readMore);

    // listen for changes of content
    this.mutationSub = this.obs.observe(this.container).subscribe(() => {
      // do nothing if the mutation is the result of the element expanding
      if (!this.isExpanding) {
        this.truncate();
      }
    });

    // listen for resize events
    this.unsubResize = this.renderer.listen('window', 'resize', () => {
      // make sure the element is displayed
      if (
        this.container &&
        (this.container.offsetWidth ||
          this.container.offsetHeight ||
          this.container.getClientRects().length)
      ) {
        const newSizes = {
          width: window.innerWidth,
          height: window.innerHeight,
        };
        // only do something if the actual window size has changed
        if (
          this.windowSize.width !== newSizes.width ||
          this.windowSize.height !== newSizes.height
        ) {
          this.windowSize = newSizes;
          if (!this.isExpanded) {
            // restore the original content
            Array.from(this.container.childNodes).forEach((node, index) => {
              if (this.isTextNode(node)) {
                node.textContent = this.originalContent[index];
              }
            });
            // truncate again
            this.truncate();
          }
        }
      }
    });
  }

  private isTextNode(node: Node): node is Text {
    return node.nodeType === Node.TEXT_NODE;
  }
}
